import React, { useState } from 'react';

import styles from './Login.module.css';

import FormLayout from './FormLayout';
import { MfaTypeV6 } from './useAuthFlow';

interface MFAFormProps {
  mfaType: MfaTypeV6;
  onSubmit: (code: string) => void;
  loading: boolean;
  errorMessage: string;
  message: string;
}

const MFAForm: React.FC<MFAFormProps> = ({
  mfaType,
  onSubmit,
  loading,
  errorMessage,
  message
}) => {
  const [code, setCode] = useState<string>('');
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onSubmit(code);
  };

  React.useEffect(() => {
    // Enable submit button only if code is exactly 6 digits
    const codeIsValid = /^\d{6}$/.test(code);
    setCanSubmit(codeIsValid);
  }, [code]);

  const getTitle = (): string => {
    switch (mfaType) {
    case 'TOTP':
      return 'Authenticator Code';
    case 'SMS':
      return 'Enter SMS Code';
    case 'EMAIL':
      return 'Email Code';
    default:
      return 'Verification Code';
    }
  };

  const getLabel = (): string => {
    return mfaType === 'TOTP' ? '6-digit code from app' : 'Verification code (6 digits)';
  };

  const codePrompt = mfaType === 'TOTP' ?
    'Enter the 6-digit code from your authenticator app.' :
    'Enter the 6-digit code sent to your device.';

  return (
    <FormLayout
      title={getTitle()}
      errorMessage={errorMessage}
      message={message}
    >
      <div style={{ paddingBottom: '12px' }}>
        {codePrompt}
      </div>

      <form onSubmit={handleSubmit}>
        <div className={styles['form-field']}>
          <label htmlFor="mfa-code">{getLabel()}: <span className={styles['label-required-star']}>*</span></label>
          <input
            id="mfa-code"
            data-testid="mfa-code-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="123456"
            required
          />
        </div>

      </form>

      <div className={styles['login-action-container']}>
        <button
          type="submit"
          className={styles['login-submit-btn']}
          data-testid="verify-btn"
          disabled={(loading || !canSubmit)}
          onClick={handleSubmit}
        >
          Verify
        </button>
      </div>
    </FormLayout>
  );
};

export default MFAForm;
